import { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { SettingRow } from '@vnetwork/ui/layout';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import type { SlaTemplateFull, SlaTier } from '@/api/services';
import { useCreateSlaTemplate, useUpdateSlaTemplate, type SlaTemplatePayload } from '@/api/services-mutations';
import { NumericInput } from '@/components/numeric-input';

const TIER_OPTIONS: { value: SlaTier; label: string }[] = [
  { value: 'bronze', label: 'Bronze' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'platinum', label: 'Platinum' },
  { value: 'custom', label: 'Custom' },
];

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Ho_Chi_Minh', label: 'Asia/Ho_Chi_Minh (UTC+7)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
];

interface FormState {
  template_name: string;
  template_tier: SlaTier;
  template_description: string;
  uptime_guarantee: string;
  downtime_allowance_minutes: string;
  default_response_time: string;
  default_resolution_time: string;
  business_hours_only: boolean;
  bh_timezone: string;
  bh_start: string;
  bh_end: string;
  is_active: boolean;
}

function blankForm(): FormState {
  return {
    template_name: '',
    template_tier: 'bronze',
    template_description: '',
    uptime_guarantee: '99',
    downtime_allowance_minutes: '432',
    default_response_time: '240',
    default_resolution_time: '480',
    business_hours_only: false,
    bh_timezone: 'Asia/Ho_Chi_Minh',
    bh_start: '08:00',
    bh_end: '17:30',
    is_active: true,
  };
}

function fromTemplate(t: SlaTemplateFull): FormState {
  return {
    template_name: t.template_name,
    template_tier: t.template_tier,
    template_description: t.template_description ?? '',
    uptime_guarantee: String(t.uptime_guarantee),
    downtime_allowance_minutes: String(t.downtime_allowance_minutes),
    default_response_time: String(t.default_response_time),
    default_resolution_time: String(t.default_resolution_time),
    business_hours_only: t.business_hours_only,
    bh_timezone: t.business_hours_config?.timezone ?? 'Asia/Ho_Chi_Minh',
    bh_start: t.business_hours_config?.work_hours?.start ?? '08:00',
    bh_end: t.business_hours_config?.work_hours?.end ?? '17:30',
    is_active: t.is_active,
  };
}

function toPayload(f: FormState): SlaTemplatePayload {
  return {
    template_name: f.template_name.trim(),
    template_tier: f.template_tier,
    template_description: f.template_description.trim() || undefined,
    uptime_guarantee: parseFloat(f.uptime_guarantee) || undefined,
    downtime_allowance_minutes: parseInt(f.downtime_allowance_minutes, 10) || undefined,
    default_response_time: parseInt(f.default_response_time, 10) || undefined,
    default_resolution_time: parseInt(f.default_resolution_time, 10) || undefined,
    business_hours_only: f.business_hours_only,
    business_hours_config: f.business_hours_only
      ? {
          timezone: f.bh_timezone,
          work_days: [1, 2, 3, 4, 5],
          work_hours: { start: f.bh_start, end: f.bh_end },
        }
      : null,
    is_active: f.is_active,
  };
}

export interface SlaTemplateDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create mode, non-null = edit mode. */
  template: SlaTemplateFull | null;
}

/**
 * Create / Edit SLA template in a right-side Drawer.
 * Fields: name, tier, description, uptime guarantee, downtime allowance,
 * default response/resolution times, business-hours toggle + config, active flag.
 * Backed by POST /sla-templates (create) and PUT /sla-templates/:id (edit).
 */
export function SlaTemplateDrawer({ open, onOpenChange, template }: SlaTemplateDrawerProps) {
  const toast = useToast();
  const isEdit = !!template;

  const createMutation = useCreateSlaTemplate();
  const updateMutation = useUpdateSlaTemplate(template?.sla_template_id ?? 0);
  const pending = createMutation.isPending || updateMutation.isPending;

  const [form, setForm] = useState<FormState>(blankForm);
  const [error, setError] = useState<string | null>(null);

  // Sync form when drawer opens or target changes
  useEffect(() => {
    if (open) {
      setForm(template ? fromTemplate(template) : blankForm());
      setError(null);
      createMutation.reset();
      updateMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validate = (): string | null => {
    if (!form.template_name.trim()) return 'Template name is required.';
    const uptime = parseFloat(form.uptime_guarantee);
    if (isNaN(uptime) || uptime <= 0 || uptime > 100) return 'Uptime guarantee must be between 0 and 100.';
    return null;
  };

  const handleSubmit = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);

    const payload = toPayload(form);
    const onSuccess = () => {
      toast.success(isEdit ? 'Template updated' : 'Template created');
      onOpenChange(false);
    };
    const onError = (e: Error) => {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    };

    if (isEdit) {
      updateMutation.mutate(payload, { onSuccess, onError });
    } else {
      createMutation.mutate(payload, { onSuccess, onError });
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>{isEdit ? 'Edit SLA Template' : 'New SLA Template'}</DrawerTitle>
          <DrawerDescription>
            {isEdit
              ? `Editing "${template?.template_name}"`
              : 'Define a reusable SLA tier with uptime, response, and resolution targets.'}
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {error && <Alert tone="danger" title={error} />}

            <Field label="Template name" required>
              {(p) => (
                <Input
                  {...p}
                  value={form.template_name}
                  onChange={(e) => set('template_name', e.target.value)}
                  placeholder="e.g. Gold SLA"
                />
              )}
            </Field>

            <Field label="Tier" required>
              {(p) => (
                <Select value={form.template_tier} onValueChange={(v) => set('template_tier', v as SlaTier)}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Description">
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  value={form.template_description}
                  onChange={(e) => set('template_description', e.target.value)}
                  placeholder="Short description of this SLA tier…"
                />
              )}
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Uptime guarantee (%)" required>
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    max={100}
                    step={0.01}
                    value={form.uptime_guarantee}
                    onChange={(e) => set('uptime_guarantee', e.target.value)}
                    placeholder="99.9"
                  />
                )}
              </Field>
              <Field label="Downtime allowance (min)">
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    value={form.downtime_allowance_minutes}
                    onChange={(e) => set('downtime_allowance_minutes', e.target.value)}
                    placeholder="432"
                  />
                )}
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Default response time (min)">
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    value={form.default_response_time}
                    onChange={(e) => set('default_response_time', e.target.value)}
                    placeholder="240"
                  />
                )}
              </Field>
              <Field label="Default resolution time (min)">
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    value={form.default_resolution_time}
                    onChange={(e) => set('default_resolution_time', e.target.value)}
                    placeholder="480"
                  />
                )}
              </Field>
            </div>

            {/* Business hours toggle */}
            <SettingRow
              title="Business hours only"
              description="Apply SLA response/resolution targets only during business hours"
              control={
                <Switch
                  checked={form.business_hours_only}
                  onCheckedChange={(v) => set('business_hours_only', v)}
                />
              }
            />

            {form.business_hours_only && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)', paddingLeft: 'var(--vn-space-2)' }}>
                <Field label="Timezone">
                  {(p) => (
                    <Select value={form.bh_timezone} onValueChange={(v) => set('bh_timezone', v)}>
                      <SelectTrigger {...p}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
                  <Field label="Work start">
                    {(p) => (
                      <Input
                        {...p}
                        type="time"
                        value={form.bh_start}
                        onChange={(e) => set('bh_start', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label="Work end">
                    {(p) => (
                      <Input
                        {...p}
                        type="time"
                        value={form.bh_end}
                        onChange={(e) => set('bh_end', e.target.value)}
                      />
                    )}
                  </Field>
                </div>
              </div>
            )}

            {/* Active toggle */}
            <SettingRow
              title="Active"
              description="Inactive templates are hidden from the service SLA template picker"
              control={
                <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} />
              }
            />
          </div>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={pending}>
            {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
